/**
 * Aspect Admin — Settings: workspace, branding, security, preferences.
 */

import { icon } from "./icons.js";
import { renderShell, pageHead, toast, dropdownHtml, bindDropdowns } from "./shell.js";
import { getSession } from "../auth_store.js";
import { navigate } from "../router.js";
import { t } from "../i18n.js";
import { WORKSPACE } from "../mock_data.js";

let state = { tab: "workspace" };

const TABS = [
  ["workspace", "Workspace", "gear"],
  ["branding", "Branding", "paint"],
  ["security", "Security", "shield"],
  ["preferences", "Preferences", "sliders"],
];

function switchRow(title, desc, on = false, attr = "") {
  return `
    <div class="ap-setrow">
      <div class="ap-setrow__text">
        <h4>${t(title)}</h4>
        <p>${t(desc)}</p>
      </div>
      <div class="ap-setrow__ctrl">
        <label class="ap-switch">
          <input type="checkbox" ${on ? "checked" : ""} ${attr} data-pref="${title}" />
          <span class="ap-switch__track"></span>
        </label>
      </div>
    </div>
  `;
}

function workspaceTab() {
  return `
    <div class="ap-panel">
      <div class="ap-panel__head"><h3 class="ap-panel__title">${t("General")}</h3></div>
      <div class="ap-panel__body">
        <div class="ap-grid ap-grid--2" style="gap:1rem;">
          <div class="ap-field">
            <label class="ap-field__label">${t("Workspace name")}</label>
            <input class="ap-input" value="${WORKSPACE.name}" />
          </div>
          <div class="ap-field">
            <label class="ap-field__label">${t("Workspace URL")}</label>
            <input class="ap-input" value="aspect.app/northwind" />
          </div>
          <div class="ap-field">
            <label class="ap-field__label">${t("Default region")}</label>
            ${dropdownHtml({
              value: t("EU West (Frankfurt)"),
              options: [t("EU West (Frankfurt)"), t("EU North (Stockholm)"), t("US East (Virginia)")],
            })}
          </div>
          <div class="ap-field">
            <label class="ap-field__label">${t("Default tour visibility")}</label>
            ${dropdownHtml({
              value: t("Link-only"),
              options: [t("Link-only"), t("Private"), t("Public")],
            })}
          </div>
        </div>
      </div>
      <div class="ap-panel__foot">
        <span>${t("Changes apply to new spaces only")}</span>
        <button type="button" class="ap-btn ap-btn--primary ap-btn--sm" data-save>${t("Save changes")}</button>
      </div>
    </div>

    <div class="ap-panel ap-mt">
      <div class="ap-panel__head"><h3 class="ap-panel__title" style="color:var(--ap-err);">${t("Danger zone")}</h3></div>
      ${["Transfer ownership", "Delete workspace"]
        .map(
          (title, i) => `
        <div class="ap-setrow">
          <div class="ap-setrow__text">
            <h4>${t(title)}</h4>
            <p>${i === 0 ? t("Hand this workspace to another owner. Requires their confirmation.") : t("Permanently removes all spaces, tours, files, and members. Cannot be undone.")}</p>
          </div>
          <div class="ap-setrow__ctrl">
            <button type="button" class="ap-btn ap-btn--danger ap-btn--sm" data-danger="${title}">${t(title)}</button>
          </div>
        </div>`
        )
        .join("")}
    </div>
  `;
}

function brandingTab() {
  return `
    <div class="ap-panel">
      <div class="ap-panel__head">
        <div>
          <h3 class="ap-panel__title">${t("Tour branding")}</h3>
          <p class="ap-panel__sub">${t("Applied to every published tour and client-facing page")}</p>
        </div>
      </div>
      <div class="ap-panel__body">
        <div class="ap-grid ap-grid--2" style="gap:1rem;">
          <div class="ap-field">
            <label class="ap-field__label">${t("Logo")}</label>
            <div class="ap-dropzone" style="padding:1.4rem 1rem;" data-logo>
              ${icon("image", 20)}
              <span style="font-size:0.8rem;"><strong>${t("Upload logo")}</strong> · ${t("SVG or PNG, min 240px")}</span>
            </div>
          </div>
          <div class="ap-stack" style="gap:1rem;">
            <div class="ap-field">
              <label class="ap-field__label">${t("Accent color")}</label>
              <div class="ap-row">
                <input class="ap-input" value="#00A8D2" style="flex:1;" />
                <span style="width:2.15rem;height:2.15rem;border-radius:10px;background:var(--ap-cyan-grad);border:1px solid var(--ap-hairline);box-shadow:var(--ap-shadow-glow);flex-shrink:0;"></span>
              </div>
            </div>
            <div class="ap-field">
              <label class="ap-field__label">${t("Custom domain")}</label>
              <input class="ap-input" placeholder="tours.yourcompany.com" />
              <span class="ap-field__hint">${t("Point a CNAME at tours.aspect.app, then verify here.")}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="ap-panel__foot">
        <span>${t("Preview updates on the next publish")}</span>
        <button type="button" class="ap-btn ap-btn--primary ap-btn--sm" data-save>${t("Save branding")}</button>
      </div>
    </div>

    <div class="ap-panel ap-mt">
      ${switchRow("Show Aspect watermark", "Display “Powered by Aspect” on published tours. Removable on Scale and above.", false)}
      ${switchRow("Client-branded emails", "Send tour share emails from your domain with your logo.", true)}
    </div>
  `;
}

function securityTab() {
  return `
    <div class="ap-panel">
      <div class="ap-panel__head"><h3 class="ap-panel__title">${t("Access & authentication")}</h3></div>
      ${switchRow("Require two-factor authentication", "All members must enroll a second factor before accessing the workspace.", true)}
      ${switchRow("Single sign-on (SAML)", "Authenticate through your identity provider. Available on Enterprise.", false, "disabled")}
      ${switchRow("Session timeout", "Sign members out after 12 hours of inactivity.", true)}
    </div>

    <div class="ap-panel ap-mt">
      <div class="ap-panel__head"><h3 class="ap-panel__title">${t("Data protection")}</h3></div>
      ${switchRow("IP allowlist", "Restrict portal access to approved network ranges.", false)}
      ${switchRow("Audit log streaming", "Stream security events to your SIEM via webhook.", true)}
      <div class="ap-setrow">
        <div class="ap-setrow__text">
          <h4>${t("Data residency")}</h4>
          <p>${t("All workspace data is stored and processed in <strong>EU West (Frankfurt)</strong>.")}</p>
        </div>
        <div class="ap-setrow__ctrl"><span class="ap-badge ap-badge--ok">${icon("check", 11)} GDPR</span></div>
      </div>
    </div>

    <div class="ap-panel ap-mt">
      <div class="ap-panel__head"><h3 class="ap-panel__title">${t("Active sessions")}</h3></div>
      ${[
        ["Chrome · macOS", "Tallinn, EE · current session", true],
        ["Safari · iPhone", "Tallinn, EE · 2 hours ago", false],
        ["Edge · Windows", "Riga, LV · 3 days ago", false],
      ]
        .map(
          ([device, meta, current]) => `
        <div class="ap-setrow">
          <div class="ap-setrow__text">
            <h4>${device} ${current ? `<span class="ap-badge ap-badge--cyan" style="margin-left:0.4rem;">${t("This device")}</span>` : ""}</h4>
            <p>${t(meta)}</p>
          </div>
          <div class="ap-setrow__ctrl">
            ${current ? "" : `<button type="button" class="ap-btn ap-btn--ghost ap-btn--sm" data-revoke>${t("Revoke")}</button>`}
          </div>
        </div>`
        )
        .join("")}
    </div>
  `;
}

function preferencesTab() {
  return `
    <div class="ap-panel">
      <div class="ap-panel__head"><h3 class="ap-panel__title">${t("Notifications")}</h3></div>
      ${switchRow("AI job completions", "Notify when generations finish or fail.", true)}
      ${switchRow("Client activity", "Tour views, engagement spikes, and churn signals.", true)}
      ${switchRow("Billing alerts", "Usage past 80% of any cap, invoices, and payment issues.", true)}
      ${switchRow("Weekly digest email", "A Monday summary of workspace performance.", false)}
    </div>

    <div class="ap-panel ap-mt">
      <div class="ap-panel__head"><h3 class="ap-panel__title">${t("AI behavior")}</h3></div>
      ${switchRow("Proactive insights", "Let Aspect surface anomalies and recommendations on the dashboard.", true)}
      ${switchRow("Auto-retry failed jobs", "Retry failed generations once with enhanced preprocessing.", true)}
      <div class="ap-setrow">
        <div class="ap-setrow__text">
          <h4>${t("Confidence threshold")}</h4>
          <p>${t("Jobs scoring below this value are flagged for review instead of auto-completing.")}</p>
        </div>
        <div class="ap-setrow__ctrl">
          ${dropdownHtml({
            value: t("0.50 — balanced"),
            options: [t("0.50 — balanced"), t("0.70 — strict"), t("0.30 — permissive")],
            className: "ap-dropdown--auto",
          })}
        </div>
      </div>
    </div>
  `;
}

export function renderSettings(root) {
  const session = getSession();
  if (!session) { navigate("/login", true); return; }

  const body =
    state.tab === "workspace" ? workspaceTab() : state.tab === "branding" ? brandingTab() : state.tab === "security" ? securityTab() : preferencesTab();

  const content = `
    ${pageHead(t("Settings"), t("Workspace configuration, branding, security, and preferences."))}
    <div class="ap-toolbar">
      <div class="ap-seg">
        ${TABS.map(([id, label, ic]) => `<button type="button" class="ap-seg__btn ${state.tab === id ? "ap-seg__btn--on" : ""}" data-tab="${id}">${icon(ic, 14)} ${t(label)}</button>`).join("")}
      </div>
    </div>
    ${body}
  `;

  const el = renderShell(root, "/settings", content);
  if (!el) return;

  el.querySelectorAll("[data-tab]").forEach((tab) =>
    tab.addEventListener("click", () => {
      state = { tab: tab.dataset.tab };
      renderSettings(root);
    })
  );

  el.querySelectorAll("[data-save]").forEach((b) => b.addEventListener("click", () => toast(root, t("Settings saved"), "ok")));
  el.querySelectorAll("[data-pref]").forEach((sw) =>
    sw.addEventListener("change", () => toast(root, `${t(sw.dataset.pref)} ${sw.checked ? t("enabled") : t("disabled")}`, "ok"))
  );
  el.querySelectorAll("[data-revoke]").forEach((b) => b.addEventListener("click", () => toast(root, t("Session revoked"), "info")));
  el.querySelectorAll("[data-danger]").forEach((b) => b.addEventListener("click", () => toast(root, t("\"{t}\" requires owner re-authentication", { t: t(b.dataset.danger) }), "info")));
  el.querySelector("[data-logo]")?.addEventListener("click", () => toast(root, t("Logo upload opening…"), "info"));
  bindDropdowns(el);
}
